<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  .uploads-tab-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    margin-top: 8px;
  }

  .uploads-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }

  .upload-area-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .file-upload-container {
      position: relative;
    }

    .drag-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 116, 228, 0.15);
      border: 2px dashed colors.get(primary_40);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      pointer-events: none;
      height: 150px;

      .drag-message {
        @include mixins.h4;
        background-color: colors.get(interfaceWhite);
        padding: 16px 32px;
        border-radius: 8px;
        color: colors.get(primary_40);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }
  }

  .project-selection-wrapper {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .project-label {
      @include mixins.body4-badge;
      color: colors.get(textBlack);
    }
  }

  .upload-button-wrapper {
    margin-top: 16px;
  }

  .upload-progress-wrapper {
    gap: 8px;

    .uploading-text {
      @include mixins.body4-badge;
      color: colors.get(textDisabledGrey);
      text-align: left;
    }
  }

  .helper-text {
    display: flex;
    align-items: center;
    gap: 8px;
    @include mixins.body4;
    color: colors.get(textDisabledGrey);
    flex-shrink: 0;
  }
</style>

<script>
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import Icon from "@sourcewhale/shared/svelte-design-system/components/atoms/Icon.svelte";
  import MultiSelectDropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/MultiSelectDropdown/MultiSelectDropdown.svelte";
  import FileUpload from "@sourcewhale/shared/svelte-design-system/components/molecules/FileUpload/FileUpload.svelte";
  import UploadProgress from "@sourcewhale/shared/svelte-design-system/components/molecules/UploadProgress/UploadProgress.svelte";
  import { onMount } from "svelte";
  import { showToast } from "@sourcewhale/shared/src/assets/js/src/general/utils";
  import { projectsStore } from "../../../../api-stores/projects-store";
  import { saveFile } from "../../../../api/files";
  import { parseResumes } from "../../../../api/resumes";
  import { ToastError } from "@sourcewhale/shared/src/svelte/api/sw-toast-error";
  import { fileToDataUrl } from "../../../../utils/files.js";
  import RESUME_FILE_CONFIG from "./assets.js";
  import {
    activeTabStore,
    isDraggingFilesStore,
    persistedFilesStore,
    selectedProjectIdsStore,
  } from "./stores";

  let isUploading = $state(false);
  let selectedFilesCount = $derived($persistedFilesStore.length);
  let progress = $state(0);
  let errorMessage = $state("");

  onMount(() => {
    projectsStore.get();
  });

  const projectOptions = $derived(
    $projectsStore.map((project) => ({
      id: project.projectId,
      label: project.projectName,
      icon: "far fa-folder",
    }))
  );

  const handleFilesSelected = ({ files }) => {
    if (Array.isArray(files)) {
      $persistedFilesStore = files;
    } else if (files) {
      $persistedFilesStore = [files];
    } else {
      $persistedFilesStore = [];
    }
  };

  const processFile = async (file) => {
    const data = await fileToDataUrl(file);
    const res = await saveFile(data, file.name);
    return res.fileId;
  };

  const handleUpload = async () => {
    event.preventDefault();

    try {
      if (selectedFilesCount === 0 || isUploading) return;

      isUploading = true;
      errorMessage = "";
      progress = 0;

      const files = $persistedFilesStore;
      const totalFiles = files.length;
      const fileIds = [];
      const filePromises = [];

      files.forEach((file) => {
        const promise = processFile(file)
          .then((fileId) => {
            fileIds.push(fileId);
            progress += Math.round((1 / totalFiles) * 100);
          })
          .catch(() => {
            showToast({
              title: "Error",
              subtitle: `Failed to upload file: ${file.name}`,
              type: "danger",
            });
          });

        filePromises.push(promise);
      });

      await Promise.all(filePromises);

      const body = {
        resumes: fileIds.map((fileId) => ({ fileId })),
        projectIds: $selectedProjectIdsStore,
        origin: window.location.pathname,
        actions: ["createCandidate"],
      };

      await parseResumes(body);
      progress = 100;

      showToast({
        title: "Resume uploaded successfully",
        subtitle: "Resume will be processed shortly",
        type: "success",
      });
      activeTabStore.set("recents");
    } catch (error) {
      if (error instanceof ToastError) {
        error.title = "Failed to upload resume";
        showToast(error);
        return;
      }
      throw error;
    } finally {
      isUploading = false;
      $persistedFilesStore = [];
    }
  };

  const uploadButtonText = $derived(
    selectedFilesCount > 0
      ? `Upload files (${selectedFilesCount})`
      : "Upload files"
  );
</script>

<div class="uploads-tab-container">
  <div class="uploads-content">
    <div class="upload-area-wrapper">
      {#if isUploading}
        <div class="upload-progress-wrapper">
          <div class="uploading-text">
            {progress === 100 ? "Files Uploaded" : "Uploading..."}
          </div>
          <UploadProgress {progress} {errorMessage} />
        </div>
      {/if}

      <div class="file-upload-container">
        {#if $isDraggingFilesStore && !isUploading}
          <div class="drag-overlay">
            <div class="drag-message">Drop Files here</div>
          </div>
        {/if}

        <FileUpload
          maxFiles={100}
          allowedTypes={RESUME_FILE_CONFIG.allowedTypes}
          maxFileSize={RESUME_FILE_CONFIG.maxFileSize}
          acceptedExtensions={RESUME_FILE_CONFIG.acceptedExtensions}
          uploadText="Drop one or more file(s) here or click to browse"
          uploadSubtext="Supports PDF or DOCX files up to 64MB each"
          invalidTypeError="Please select a PDF or Word document"
          fileTooLargeError="File size must be less than 64MB"
          {isUploading}
          bind:selectedFiles={$persistedFilesStore}
          onFilesSelected={handleFilesSelected}
        />
      </div>
    </div>

    {#if !isUploading && selectedFilesCount > 0}
      <div class="project-selection-wrapper">
        <MultiSelectDropdown
          placeholder="Select projects"
          items={projectOptions}
          bind:selectedItems={$selectedProjectIdsStore}
          showFilter
          dropdownStyle="outline: none; width: 100%;"
        />
      </div>

      <div class="upload-button-wrapper">
        <Button
          kind="primary"
          size="small"
          disabled={selectedFilesCount === 0}
          onclick={handleUpload}
          class="full-width"
        >
          <Icon class="fa-regular fa-arrow-up-from-bracket" size="small" />
          {uploadButtonText}
        </Button>
      </div>
    {/if}
  </div>
  <div class="helper-text">
    <Icon class="fa-solid fa-circle-info" size="small" />
    <span
      >Upload resumes to create contacts. Existing contacts will be updated with
      new information found in the resume.</span
    >
  </div>
</div>
